import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BackToTop from "@/components/ui/BackToTop";
import {
  ArrowRight,
  Globe,
  Shield,
  Users,
  Eye,
  Heart,
  TrendingUp,
  Award,
  MapPin,
  Calendar,
  ExternalLink,
  Briefcase,
  CheckCircle,
  Sparkles,
  Building2,
  GraduationCap,
  Newspaper,
} from "lucide-react";
import { useEffect } from "react";

const About = () => {
  useEffect(() => {
    document.title =
      "About NationCard™ - Empowering Decisions with Verified Public Sentiment";
  }, []);

  const milestones = [
    {
      year: "2020",
      title: "Founded",
      description:
        "NationCard™ was born from a vision to democratize access to authentic public sentiment data",
      icon: Sparkles,
    },
    {
      year: "2021",
      title: "First Deployment",
      description:
        "Launched our verified polling platform with initial focus on civic engagement",
      icon: MapPin,
    },
    {
      year: "2022",
      title: "Global Expansion",
      description:
        "Expanded to 50+ countries with multilingual support and regional partnerships",
      icon: Globe,
    },
    {
      year: "2023",
      title: "Academic Partnerships",
      description:
        "Established research collaborations with leading universities and think tanks",
      icon: GraduationCap,
    },
    {
      year: "2024",
      title: "100+ Countries",
      description:
        "Reached global coverage with over 1M verified participants worldwide",
      icon: Users,
    },
  ];

  const values = [
    {
      icon: Eye,
      title: "Transparency",
      description:
        "Open, anonymized data you can trust with full methodology disclosure",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: Shield,
      title: "Integrity",
      description:
        "Verified participants and rigorous quality controls ensure research excellence",
      gradient: "from-cyan-500 to-teal-500",
    },
    {
      icon: TrendingUp,
      title: "Impact",
      description:
        "Insights that power real-world change in policy, campaigns, and research",
      gradient: "from-teal-500 to-blue-500",
    },
  ];

  const partners = [
    {
      name: "UN Development Programme",
      category: "International Organizations",
    },
    { name: "Oxford University", category: "Academic Institutions" },
    { name: "Transparency International", category: "NGOs" },
    { name: "European Commission", category: "Government Bodies" },
    { name: "Pew Research Center", category: "Research Organizations" },
    { name: "Freedom House", category: "Advocacy Groups" },
  ];

  const pressFeatures = [
    {
      outlet: "TechCrunch",
      headline: "NationCard™ Revolutionizes Public Opinion Research",
      date: "March 2024",
      link: "#",
    },
    {
      outlet: "Reuters",
      headline: "How Verified Polling is Changing Democracy",
      date: "February 2024",
      link: "#",
    },
    {
      outlet: "The Guardian",
      headline: "Fighting Disinformation with Authentic Data",
      date: "January 2024",
      link: "#",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Hero & Mission Section */}
      <section className="relative pt-[97px] pb-16 md:pt-[110px] md:pb-24 overflow-hidden bg-gradient-to-br from-blue-900 via-cyan-900 to-teal-900">
        {/* Background overlay */}
        <div className="absolute inset-0 opacity-20 bg-[url('/img/hero-13-1920x480.png')] bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-cyan-900/85 to-teal-900/90"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="mb-8 inline-flex items-center">
              <Badge
                variant="outline"
                className="bg-white/10 text-white border-white/30 px-4 py-2 text-sm backdrop-blur-sm"
              >
                <Globe size={16} className="mr-2" />
                <span>Trusted by Organizations Worldwide</span>
              </Badge>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-8">
              Empowering Decisions with{" "}
              <span className="text-cyan-300">Verified</span> Public{" "}
              <span className="text-teal-300">Sentiment</span>
            </h1>

            <p className="text-xl md:text-2xl opacity-90 leading-relaxed mb-12 max-w-3xl mx-auto">
              NationCard™ gives organizations and citizens real-time insights
              to drive smarter policy, campaigns, and research with authentic
              public opinion data.
            </p>

            {/* Visual representation */}
            <div className="relative mx-auto max-w-2xl">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
                <div className="grid grid-cols-3 gap-6 items-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Globe className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-sm opacity-80">Global Reach</div>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Shield className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-sm opacity-80">Verified Data</div>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <TrendingUp className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-sm opacity-80">Real Impact</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Our Story
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From a small team with a big vision to a global platform trusted
                by organizations worldwide
              </p>
            </div>

            {/* Timeline */}
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-blue-500 via-cyan-500 to-teal-500 h-full hidden md:block"></div>

              <div className="space-y-12">
                {milestones.map((milestone, index) => (
                  <div
                    key={milestone.year}
                    className={`flex flex-col md:flex-row items-center gap-8 ${
                      index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    {/* Content */}
                    <div className="flex-1 md:text-right md:pr-8 text-center">
                      {index % 2 !== 0 && (
                        <div className="md:text-left md:pl-8 md:pr-0"></div>
                      )}
                      <Card className="hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4 justify-center md:justify-start">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                              <milestone.icon className="w-5 h-5 text-white" />
                            </div>
                            <Badge
                              variant="secondary"
                              className="font-semibold"
                            >
                              {milestone.year}
                            </Badge>
                          </div>
                          <h3 className="text-xl font-bold text-foreground mb-2">
                            {milestone.title}
                          </h3>
                          <p className="text-muted-foreground">
                            {milestone.description}
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Timeline dot */}
                    <div className="hidden md:flex w-6 h-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full border-4 border-background shadow-lg z-10"></div>

                    {/* Spacer for alternating layout */}
                    <div className="flex-1"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Founder Quote */}
            <div className="mt-16">
              <Card className="max-w-4xl mx-auto bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/50 dark:to-cyan-950/50 border-blue-200 dark:border-blue-800">
                <CardContent className="p-8 text-center">
                  <blockquote className="text-xl italic text-foreground mb-6">
                    "We founded NationCard™ because democracy works best when
                    it's informed by authentic voices, not amplified noise. Our
                    mission is to ensure every decision-maker has access to the
                    real pulse of the people."
                  </blockquote>
                  <div className="text-sm text-muted-foreground">
                    — The NationCard™ Founding Team
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Our Mission & Values
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Three core principles guide everything we do at NationCard™
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <Card
                  key={index}
                  className="group hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 text-center"
                >
                  <CardHeader className="pb-4">
                    <div
                      className={`h-20 w-20 bg-gradient-to-br ${value.gradient} rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform`}
                    >
                      <value.icon className="h-10 w-10 text-white" />
                    </div>
                    <CardTitle className="text-2xl text-foreground">
                      {value.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-lg">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partners & Press Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Partners */}
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Trusted Partners
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-12">
                We work with leading organizations worldwide to democratize
                access to authentic public sentiment
              </p>

              {/* Partner logos grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-8 mb-16">
                {partners.map((partner, index) => (
                  <div key={index} className="group">
                    <Card className="hover:shadow-lg transition-shadow duration-300 h-full">
                      <CardContent className="p-6 text-center h-full flex flex-col justify-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900 dark:to-cyan-900 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                          <Building2 className="w-8 h-8 text-blue-600" />
                        </div>
                        <h3 className="font-semibold text-foreground mb-2">
                          {partner.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {partner.category}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>

            {/* Press Features */}
            <div className="text-center">
              <h3 className="text-2xl font-bold text-foreground mb-8">
                In the News
              </h3>

              <div className="grid md:grid-cols-3 gap-6">
                {pressFeatures.map((feature, index) => (
                  <Card
                    key={index}
                    className="group hover:shadow-lg transition-all duration-300 cursor-pointer"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900 dark:to-orange-900 rounded-lg flex items-center justify-center">
                          <Newspaper className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="text-left">
                          <div className="font-semibold text-foreground text-sm">
                            {feature.outlet}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {feature.date}
                          </div>
                        </div>
                      </div>
                      <h4 className="font-semibold text-foreground mb-3 text-left group-hover:text-blue-600 transition-colors">
                        {feature.headline}
                      </h4>
                      <div className="flex items-center justify-between">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-0 h-auto"
                        >
                          Read Article
                          <ExternalLink className="ml-2 h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Careers Teaser Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
              <CardContent className="p-0">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="p-8 md:p-12">
                    <div className="flex items-center gap-3 mb-6">
                      <Briefcase className="w-8 h-8" />
                      <h2 className="text-3xl font-bold">
                        Join the NationCard™ Team
                      </h2>
                    </div>
                    <p className="text-xl mb-8 text-blue-100">
                      Help us democratize access to authentic public sentiment.
                      We're looking for passionate individuals who believe in
                      the power of verified data.
                    </p>
                    <div className="space-y-4 mb-8">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-cyan-300" />
                        <span>Remote-first culture</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-cyan-300" />
                        <span>Competitive benefits</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-cyan-300" />
                        <span>Meaningful impact</span>
                      </div>
                    </div>
                    <Button
                      size="lg"
                      variant="secondary"
                      className="bg-white text-blue-600 hover:bg-gray-100"
                    >
                      View Open Roles
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                  <div className="relative min-h-[300px] bg-gradient-to-br from-blue-500/20 to-cyan-500/20">
                    {/* Placeholder for team image */}
                    <div className="absolute inset-0 bg-[url('/img/hero-26-1920x480.png')] bg-cover bg-center opacity-30"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-white/80">
                        <Users className="w-24 h-24 mx-auto mb-4" />
                        <p className="text-lg">Diverse, Remote Team</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-800 via-blue-900 to-indigo-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Want to Learn More?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Discover how NationCard™ can provide your organization with the
              authentic public sentiment data you need to make informed
              decisions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="secondary"
                className="text-lg px-8 py-4 bg-cyan-500 text-slate-900 hover:bg-cyan-400 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                onClick={() => (window.location.href = "/contact")}
              >
                Request a Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-4 border-white text-white hover:bg-white/10 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                onClick={() => (window.location.href = "/solutions/government")}
              >
                Explore Solutions
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default About;
